/**
 * Demo Role Persistence Utility
 * 
 * Manages role configurations in Demo Mode with proper reset behavior:
 * 
 * DEMO MODE:
 * - Roles start with default configurations from ROLE_TEMPLATES
 * - Users CAN modify roles during a session (for exploration/testing)
 * - Changes persist ONLY in memory (until page refresh)
 * - On page refresh/reload → RESET to ROLE_TEMPLATES defaults
 * - This ensures every demo session starts clean
 * 
 * LIVE MODE:
 * - Roles are stored in backend database
 * - Changes persist permanently
 * - Never reset unless explicitly requested
 * 
 * Implementation:
 * - Uses in-memory storage ONLY (no sessionStorage/localStorage)
 * - Page refresh clears memory and reloads from ROLE_TEMPLATES
 * - ROLE_TEMPLATES are the "source of truth" for demo defaults
 */

import { Role, ROLE_TEMPLATES } from '@/types/permissions';

// In-memory storage for demo roles (clears on page refresh)
let demoRolesCache: Role[] | null = null;

// Listeners for demo role changes (for reactive updates)
type RoleChangeListener = () => void;
const roleChangeListeners: RoleChangeListener[] = [];

/**
 * Subscribe to demo role changes
 * Returns unsubscribe function
 */
export function subscribeToRoleChanges(listener: RoleChangeListener): () => void {
  roleChangeListeners.push(listener);
  return () => {
    const index = roleChangeListeners.indexOf(listener);
    if (index > -1) {
      roleChangeListeners.splice(index, 1);
    }
  };
}

/**
 * Notify all listeners that roles have changed
 */
function notifyRoleChanges(): void {
  roleChangeListeners.forEach(listener => listener());
}

/**
 * Get demo roles for current session
 * 
 * On first call (or after page refresh):
 * - Returns ROLE_TEMPLATES (clean defaults)
 * - Stores them in memory cache
 * 
 * On subsequent calls within same page session:
 * - Returns modified roles from memory cache
 * 
 * Page refresh clears memory, so every refresh starts with clean ROLE_TEMPLATES.
 * This allows editing within a session while ensuring every page load starts fresh.
 */
export function getDemoRoles(): Role[] {
  // If cache is empty (first call or after page refresh), initialize from ROLE_TEMPLATES
  if (demoRolesCache === null) {
    console.log('Initializing demo roles from ROLE_TEMPLATES (fresh page load)');
    demoRolesCache = JSON.parse(JSON.stringify(ROLE_TEMPLATES)) as Role[]; // Deep clone
  }
  
  return demoRolesCache;
}

/**
 * Save demo role changes to current session
 * 
 * Stores modified role in memory cache so changes persist
 * within the current page session (until refresh).
 */
export function saveDemoRole(updatedRole: Role): void {
  console.log('saveDemoRole called with:', updatedRole.name, 'permissions:', updatedRole.permissions.length);
  const currentRoles = getDemoRoles();
  const updatedRoles = currentRoles.map(role => 
    role.id === updatedRole.id ? updatedRole : role
  );
  demoRolesCache = updatedRoles;
  console.log('Demo roles cache updated. Cache now has', demoRolesCache.length, 'roles');
  console.log('Updated role in cache:', demoRolesCache.find(r => r.id === updatedRole.id)?.permissions.length, 'permissions');
  
  // Notify all listeners that roles have changed
  notifyRoleChanges();
}

/**
 * Get a specific demo role by ID
 */
export function getDemoRole(roleId: string): Role | undefined {
  const roles = getDemoRoles();
  return roles.find(role => role.id === roleId);
}

/**
 * Reset demo roles to defaults (ROLE_TEMPLATES)
 * 
 * This is called:
 * - Automatically on page refresh (memory clears)
 * - Manually if user clicks "Reset to Defaults" button
 */
export function resetDemoRolesToDefaults(): void {
  demoRolesCache = null;
  // Next call to getDemoRoles() will reload from ROLE_TEMPLATES
}

/**
 * Check if demo roles have been modified in current session
 */
export function haveDemoRolesBeenModified(): boolean {
  if (demoRolesCache === null) return false;
  
  // Compare with ROLE_TEMPLATES
  return JSON.stringify(demoRolesCache) !== JSON.stringify(ROLE_TEMPLATES);
}

/**
 * Get permission count for a role
 * Handles both flat permissions and nested sub-permissions
 * Counts parent permissions + their checked children
 */
export function getRolePermissionCount(role: Role): number {
  // Simply return the length of the permissions array
  // This counts all checked permissions (both parents and children)
  return role.permissions.length;
}
